import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiServices';
import globals from '../../services/globals';
import Toast from '../actions/Toasts';
import { useTranslation } from 'react-i18next';
import useConfirm from '../../hooks/useConfirm';

const alert = { open: false, message: '', variant: 'info' };

const statusColors = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'error',
};

const statusLabels = {
  pending: 'Në Pritje',
  in_progress: 'Në Progres',
  completed: 'E Kompletuar',
  cancelled: 'E Anuluar',
};

const priorityColors = {
  low: 'default',
  medium: 'primary',
  high: 'warning',
  urgent: 'error',
};

const priorityLabels = {
  low: 'E Ulët',
  medium: 'Mesatare',
  high: 'E Lartë',
  urgent: 'Urgjente',
};

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
};

const formatMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === '') return '-';
  const total = Number(minutes);
  if (!Number.isFinite(total)) return '-';
  const rounded = Math.max(0, Math.round(total));
  const hrs = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (!hrs && !mins) return '0m';
  return `${hrs ? `${hrs}h ` : ''}${mins}m`.trim();
};

const minutesToHours = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === '') return '';
  const total = Number(minutes);
  if (!Number.isFinite(total)) return '';
  return String(Math.round((total / 60) * 100) / 100);
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('sq-AL');
};

export default function TasksList() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [toast, setToast] = useState(alert);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assigned_to: '',
    department_id: '',
    due_date: '',
    planned_start: '',
    planned_end: '',
    planned_hours: '',
  });
  const currentUser = globals().getLogedInUser();
  const currentUserId = currentUser?.data?.id || currentUser?.id || null;

  // client-side timer: { taskId, startedAt, elapsed }
  const [timer, setTimer]  = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timer) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev ? { ...prev, elapsed: (Date.now() - prev.startedAt.getTime()) / 1000 } : null);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timer?.taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTasks();
    loadUsers();
    loadDepartments();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await apiService().get('tasks');
      setTasks(response.data || []);
    } catch (error) {
      setToast({ open: true, message: t('tasks.errorLoadTasks'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService().get('cms_users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await apiService().get('departments');
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const handleOpenDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'pending',
        priority: task.priority || 'medium',
        assigned_to: task.assigned_to || '',
        department_id: task.department_id || '',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        planned_start: toDatetimeLocal(task.planned_start),
        planned_end: toDatetimeLocal(task.planned_end),
        planned_hours: minutesToHours(task.planned_minutes),
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        assigned_to: '',
        department_id: '',
        due_date: '',
        planned_start: '',
        planned_end: '',
        planned_hours: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTask(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.title) {
        setToast({ open: true, message: t('tasks.titleRequired'), variant: 'error' });
        return;
      }

      const payload = {
        ...formData,
        created_by: editingTask ? undefined : currentUserId || undefined,
      };
      if (formData.planned_hours !== '') {
        const hours = Number(formData.planned_hours);
        payload.planned_minutes = Number.isFinite(hours) ? Math.round(hours * 60) : undefined;
      }
      delete payload.planned_hours;
      payload.planned_start = formData.planned_start || null;
      payload.planned_end = formData.planned_end || null;

      if (editingTask) {
        payload.id = editingTask.id;
        await apiService().put('tasks/' + editingTask.id, payload);
        setToast({ open: true, message: t('tasksList.taskUpdatedSuccess'), variant: 'success' });
      } else {
        await apiService().post('tasks', payload);
        setToast({ open: true, message: t('tasksList.taskCreatedSuccess'), variant: 'success' });
      }

      handleCloseDialog();
      loadTasks();
    } catch (error) {
      setToast({ open: true, message: t('tasks.errorSaveTask'), variant: 'error' });
    }
  };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Fshi detyrën', message: t('tasksList.confirmDeleteMsg'), confirmLabel: 'Fshi' }))) return;
    try {
      await apiService().delete('tasks', id);
      setToast({ open: true, message: t('tasksList.taskDeletedSuccess'), variant: 'success' });
      loadTasks();
    } catch (error) {
      setToast({ open: true, message: t('tasksList.errorDeleteTask'), variant: 'error' });
    }
  };

  const handleMarkComplete = async (task) => {
    try {
      await apiService().put('tasks', { status: 'completed' }, task.id);
      setToast({ open: true, message: t('tasksList.taskMarkedComplete'), variant: 'success' });
      loadTasks();
    } catch (error) {
      setToast({ open: true, message: t('tasksList.errorUpdateTask'), variant: 'error' });
    }
  };

  const handleStartTimer = async (task) => {
    if (!currentUserId) {
      setToast({ open: true, message: t('tasksList.mustBeLoggedInStart'), variant: 'warning' });
      return;
    }
    try {
      await apiService().post(`tasks/${task.id}/time-logs/start`, { user_id: currentUserId });
      setToast({ open: true, message: t('tasksList.timerStarted'), variant: 'success' });
      loadTasks();
    } catch (error) {
      setToast({ open: true, message: t('tasksList.errorStartTimer'), variant: 'error' });
    }
  };

  const handleStopTimer = async (task) => {
    if (!currentUserId) {
      setToast({ open: true, message: t('tasksList.mustBeLoggedInStop'), variant: 'warning' });
      return;
    }
    try {
      await apiService().post(`tasks/${task.id}/time-logs/stop`, { user_id: currentUserId });
      setToast({ open: true, message: t('tasksList.timerStopped'), variant: 'success' });
      loadTasks();
    } catch (error) {
      setToast({ open: true, message: t('tasksList.errorStopTimer'), variant: 'error' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('sq-AL');
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {confirmUI}
      <Toast {...toast} />
      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, '&:last-child': { pb: { xs: 1.5, sm: 2, md: 3 } } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: { xs: 2, sm: 3 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 600
              }}
            >
              {t('tasksList.title')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />}
              onClick={() => handleOpenDialog()}
              size={window.innerWidth < 600 ? 'small' : 'medium'}
              sx={{ 
                minWidth: { xs: 'auto', sm: 'auto' },
                px: { xs: 2, sm: 3 }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {t('tasksList.addNewTask')}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                {t('common.add')}
              </Box>
            </Button>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: { xs: 1, sm: 2 } }}>{t('tasksList.colTitle')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, py: { xs: 1, sm: 2 } }}>{t('tasks.colStatus')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, py: { xs: 1, sm: 2 } }}>{t('tasks.colPriority')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, py: { xs: 1, sm: 2 } }}>{t('tasksList.colAssignedTo')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, py: { xs: 1, sm: 2 } }}>{t('tasksList.colDepartment')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' }, py: { xs: 1, sm: 2 } }}>{t('tasksList.colPlanning')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' }, py: { xs: 1, sm: 2 } }}>{t('tasksList.colActualTime')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, py: { xs: 1, sm: 2 } }}>{t('tasks.colDue')}</TableCell>
                  <TableCell align="right" sx={{ py: { xs: 1, sm: 2 } }}>{t('tasks.colActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>
                      <Typography variant="body1">{task.title}</Typography>
                      {task.description && (
                        <Typography variant="caption" color="text.secondary">
                          {task.description.substring(0, 50)}
                          {task.description.length > 50 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t('tasksList.status.' + task.status)}
                        color={statusColors[task.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t('tasks.priority.' + (task.priority || 'medium'))}
                        color={priorityColors[task.priority]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{task.assigned_to_name || '-'}</TableCell>
                    <TableCell>{task.department_name || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatMinutes(task.planned_minutes)}
                      </Typography>
                      {(task.planned_start || task.planned_end) && (
                        <Typography variant="caption" color="text.secondary">
                          {task.planned_start ? formatDateTime(task.planned_start) : '—'}
                          {task.planned_end ? ` – ${formatDateTime(task.planned_end)}` : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatMinutes(task.actual_minutes)}</TableCell>
                    <TableCell>{formatDate(task.due_date)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleStartTimer(task)}
                        disabled={task.is_tracking || task.status === 'completed' || task.status === 'cancelled'}
                        title={t('tasksList.startTimeTracking')}>
                        <PlayIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleStopTimer(task)}
                        disabled={!task.is_tracking}
                        title={t('tasksList.stopTimeTracking')}>
                        <StopIcon />
                      </IconButton>
                      {task.status !== 'completed' && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleMarkComplete(task)}
                          title={t('tasks.markComplete')}>
                          <CheckIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(task)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(task.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary">
                        {t('tasksList.noTasksToShow')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
        {editingTask ? t('tasks.editTask') : t('tasksList.addNewTask')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('tasksList.colTitle')}
              fullWidth
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <TextField
              label={t('tasks.description')}
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>{t('tasks.colStatus')}</InputLabel>
              <Select
                value={formData.status}
                label={t('tasks.colStatus')}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem key="pending" value="pending">{t('tasksList.status.pending')}</MenuItem>
                <MenuItem key="in_progress" value="in_progress">{t('tasksList.status.in_progress')}</MenuItem>
                <MenuItem key="completed" value="completed">{t('tasksList.status.completed')}</MenuItem>
                <MenuItem key="cancelled" value="cancelled">{t('tasksList.status.cancelled')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('tasks.priority.title')}</InputLabel>
              <Select
                value={formData.priority}
                label={t('tasks.priority.title')}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <MenuItem key="low" value="low">{t('tasks.priority.low')}</MenuItem>
                <MenuItem key="medium" value="medium">{t('tasks.priority.medium')}</MenuItem>
                <MenuItem key="high" value="high">{t('tasks.priority.high')}</MenuItem>
                <MenuItem key="urgent" value="urgent">{t('tasks.priority.urgent')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('tasksList.colAssignedTo')}</InputLabel>
              <Select
                value={formData.assigned_to}
                label={t('tasksList.colAssignedTo')}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              >
                <MenuItem key="none" value="">
                  <em>{t('tasks.none')}</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name || user.user_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('tasksList.colDepartment')}</InputLabel>
              <Select
                value={formData.department_id}
                label={t('tasksList.colDepartment')}
                onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
              >
                <MenuItem key="none" value="">
                  <em>{t('tasks.none')}</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('tasksList.plannedHours')}
              type="number"
              fullWidth
              inputProps={{ step: 0.25, min: 0 }}
              value={formData.planned_hours}
              onChange={(e) => setFormData({ ...formData, planned_hours: e.target.value })}
            />
            <TextField
              label={t('tasksList.plannedStart')}
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.planned_start}
              onChange={(e) => setFormData({ ...formData, planned_start: e.target.value })}
            />
            <TextField
              label={t('tasksList.plannedEnd')}
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.planned_end}
              onChange={(e) => setFormData({ ...formData, planned_end: e.target.value })}
            />
            <TextField
              label={t('tasks.colDue')}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
